import {
  Component,
  HostListener,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { forkJoin, mergeMap, Subscription } from 'rxjs';
import { UserInfo } from 'src/app/models/user-info.model';
import { UsersService } from 'src/app/services/users/users.service';
import {
  SideNavToggle,
  SidenavComponent,
} from '../components/sidenav/sidenav.component';
import { SettingsService } from 'src/app/services/settings/settings.service';
import { ImageService } from 'src/app/services/profile-picture/images.service';
import {
  MatDrawerContainer,
  MatDrawer,
  MatDrawerContent,
} from '@angular/material/sidenav';
import { NgClass } from '@angular/common';
import { DefaultClassDirective } from '@ngbracket/ngx-layout/extended';
import { ToolbarComponent } from '../components/toolbar/toolbar.component';
import { ContentComponent } from '../components/content/content.component';

@Component({
  selector: 'app-logged-layout',
  templateUrl: './logged-layout.component.html',
  styleUrls: ['./logged-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatDrawerContainer,
    MatDrawer,
    SidenavComponent,
    MatDrawerContent,
    NgClass,
    DefaultClassDirective,
    ToolbarComponent,
    ContentComponent,
  ],
})
export class LoggedLayoutComponent implements OnChanges, OnInit, OnDestroy {
  private subscriptions: Subscription = new Subscription();

  screenWidth: number = window.innerWidth;
  isSideNavCollapsed = true;
  sideBarOpen = true;
  isSmallScreen = false;
  selectedEtablissement = '';
  selectedFileUrl: string | null = null;
  currentUser: UserInfo | null = null;

  constructor(
    router: Router,
    private userService: UsersService,
    private _settingsService: SettingsService,
    private _imageService: ImageService
  ) {
    //Trigger when user change page
    this.subscriptions.add(
      router.events.subscribe(val => {
        //At the end of the event
        if (val instanceof NavigationEnd) {
          this.isSmallScreen = window.innerWidth <= 1280 ? false : true;
          this.verifySize();
        }
      })
    );
    this.verifySize();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges() {
    this.screenWidth = window.innerWidth;
    this.isSideNavCollapsed = true;
  }

  ngOnInit(): void {
    this.userService
      .getCurrentUserFromStorage()
      .pipe(
        mergeMap(user => {
          this.currentUser = user;
          return forkJoin({
            settings: this._settingsService.getUserSettingsFromStorage(
              user.oid
            ),
            profilePicture: this._imageService.getProfilePicture(user.oid),
          });
        })
      )
      .subscribe({
        next: result => {
          if (result.settings) {
            this.selectedEtablissement = result.settings.defaultGE;
          } else {
            this.selectedEtablissement = this.currentUser.groupEtablissement[0];
            this._settingsService.updateCurrentGE(this.selectedEtablissement);
          }
          if (result.profilePicture) {
            this.selectedFileUrl = result.profilePicture;
          }
        },
      });

    this.subscriptions.add(
      this._settingsService.currentGE$.subscribe(ge => {
        this.selectedEtablissement = ge;
      })
    );

    // Watch for profile picture change events
    this.subscriptions.add(
      this._imageService.imageUrl$.subscribe(url => {
        this.selectedFileUrl = url;
      })
    );
  }

  //Trigger when size of screen changed
  @HostListener('window:resize')
  onResize() {
    this.verifySize();
  }

  /**
   * @description
   * Retrieves css class depending on sidenav toggle status and screen width.
   *
   * @returns A string containing the css class or an empty string.
   */
  getBodyClass(): string {
    let styleClass = '';
    if (this.isSideNavCollapsed) {
      if (this.screenWidth > 768) {
        styleClass = 'body-trimmed';
      } else if (this.screenWidth <= 768 && this.screenWidth > 0) {
        styleClass = 'body-md-screen';
      }
    }
    return styleClass;
  }

  /**
   * @description
   * Checks window size on `window.resize` events and toggle sidenav accordingly
   */
  private verifySize() {
    if (!this.isSmallScreen && window.innerWidth <= 1280) {
      this.sideBarOpen = false;
      this.isSmallScreen = true;
    } else if (window.innerWidth > 1280 && this.isSmallScreen) {
      this.sideBarOpen = true;
      this.isSmallScreen = false;
    }
  }

  /**
   * @description
   * Toggle sidenav
   */
  sideNavToggler() {
    this.sideBarOpen = !this.sideBarOpen;
  }

  //When sidenav toggled
  /**
   * @description
   * Update `screenWidth` and `isSideNavCollapsed` properties on sidenav toggle events.
   *
   * @param data New values
   */
  onToggleSidenav(data: SideNavToggle) {
    this.screenWidth = data.screenWidth;
    this.isSideNavCollapsed = data.collapsed;
  }
}
